import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'Src/components/Button/Button';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import propz from 'propz';
import {
  getName,
  getNameByPlaceOrPostcode,
  searchFunctionEventParticipants,
  searchFunctionAllHousesNinHouseIds,
  searchFunctionSportsWithFavorite,
  searchFunctionIndividualSports,
  searchFunctionTeamSports
} from 'Src/helpers/autocomplete/autocomplete';
import { resolveAgeNamingFromContext, formatAgesCompact } from 'Src/helpers/ageGroups';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { AppUser } from 'Src/views/App/App';
import {
  EVENT_ADD_TEAM,
  EVENT_TYPES,
  EVENT_TYPES_SERVER_TO_CLIENT_MAPPING,
  AGGREGATION_AND_EVENT_TYPES_MAPPING,
  AGGREGATION_AND_EVENT_TYPES_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/event';
import { Map } from 'Src/components/Map/Map';
import { Point } from 'Src/models/venue';
import { VENUE_DISTANCE, VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/venue';
import { createVenue } from 'Src/helpers/service/admin/venues';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { VenueForm } from 'Src/views/GenericView/AdminView/School/Venues/VenueForm/VenueForm';
import { SchoolForm } from 'Src/models/form';
import { TYPE_OF_PLAYERS } from '../../../../../../consts/sport';
import {
  getAgesForMultiSelect,
  getAgesFromMultiSelect,
  getDefaultGendersForMultiSelect,
  getGendersForMultiSelect,
  getGendersFromMultiSelect
} from '../../../../../../helpers/multiselect/multiselect';
import { Switch } from '../../../../../../components/Switch/Switch';
import { SchoolEventFromForm } from '../SchoolEventManager';
import {
  convertPlaceToPostcodeWithPlaceName,
  getDefaultPoint,
  getPostcodes,
  isPlace
} from '../../../../../../helpers/venue/venue';
import './SchoolEventForm.scss';
import { getAllHouses } from '../../../../../../helpers/service/admin/houses';
import moment from 'moment';
import { isClubEvent } from '../../../../../../helpers/event/event';
import { LEAGUE_AGGREGATION_TYPE } from 'Src/consts/league';
import { SchoolHouse } from 'Src/models/house';
import { PageHelpDefinition, PageHelpRegistration, registerPageHelpDefinition } from 'Src/helpers/pageHelp/pageHelp';

interface Props {
  schoolEvent?: SchoolEventFromForm;
  forms: SchoolForm[];
  onSubmit: (data) => void;
  onCancel: () => void;
  onValuesChange?: (values: any) => void;
  user: AppUser;
  createDate: Date;
  aggregationType?: string;
}

const EventDraftObserver: React.FunctionComponent<{
  values: any;
  onChange?: (values: any) => void;
}> = ({ values, onChange }) => {
  React.useEffect(() => {
    if (onChange) onChange(values);
  }, [values, onChange]);

  return null;
};

interface EventDetailsPageHelpProps {
  values: any;
  venue: any;
  user: AppUser;
  isEditing: boolean;
  isShowAllSports: boolean;
  isActivitySourceToggleVisible: boolean;
  isShortDescriptionVisible: boolean;
  isEventTypeLocked: boolean;
}

const getEventHelpTime = (value: Date | undefined): string => {
  return value && moment(value).isValid() ? moment(value).format('HH:mm') : 'not set';
};

const scrollToEventHelpAnchor = (anchorId: string): void => {
  const element = document.querySelector(`[data-help-anchor="${anchorId}"]`) as HTMLElement | null;
  if (!element) {
    return;
  }
  element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  const control = (element.querySelector('input') ||
    element.querySelector('select') ||
    element.querySelector('button')) as HTMLElement | null;
  if (control) {
    window.setTimeout(() => control.focus(), 320);
  }
};

interface EventDetailsRequiredAction {
  id: string;
  label: string;
  buttonLabel: string;
  description: string;
}

const isEventVenueSelected = (venue: any): boolean =>
  Boolean(venue && (venue.id || venue.name || venue.postcode || venue.postcodeId || venue.point));

const getEventDetailsRequiredActions = (values: any): EventDetailsRequiredAction[] => {
  const actions: EventDetailsRequiredAction[] = [];
  const isActivitySelected = Boolean(values.sportId);
  const selectedGenders = (values.gender || []).filter(item => item.isChecked);
  const startTime = moment(values.startTime);
  const endTime = moment(values.endTime);
  const isTimeRangeValid = startTime.isValid() && endTime.isValid() && !startTime.isAfter(endTime);
  const isOpponentRequired =
    values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && (values.participants || []).length === 0;

  if (!isTimeRangeValid) {
    actions.push({
      id: 'event-details-time',
      label: 'Correct the event times',
      buttonLabel: 'Correct event times to continue',
      description: 'Start and finish must be valid, and finish cannot be earlier than start.'
    });
  }
  if (!isActivitySelected) {
    actions.push({
      id: 'event-details-activity',
      label: 'Choose an Activity / Sport',
      buttonLabel: 'Choose activity to continue',
      description: 'The activity determines the team structure and which gender and age options are available.'
    });
  }
  if (isActivitySelected && selectedGenders.length === 0) {
    actions.push({
      id: 'event-details-gender',
      label: 'Choose at least one gender',
      buttonLabel: 'Choose gender to continue',
      description: 'Select both options when this is a mixed event.'
    });
  }
  if (!values.eventType) {
    actions.push({
      id: 'event-details-type',
      label: 'Choose an event type',
      buttonLabel: 'Choose event type to continue',
      description: 'Choose Inter-schools, House or Internal so the platform can build the correct participant step.'
    });
  }
  if (values.eventType === EVENT_TYPES.INTERNAL_HOUSES && (values.houses || []).length < 2) {
    actions.push({
      id: 'event-details-participants',
      label: 'Choose at least two houses',
      buttonLabel: 'Add two houses to continue',
      description: 'A house event needs at least two participating houses.'
    });
  }
  if (isOpponentRequired) {
    actions.push({
      id: 'event-details-participants',
      label: 'Choose an opponent school',
      buttonLabel: 'Add an opponent to continue',
      description: 'Choose the school you are playing before setting the venue.'
    });
  }
  if (!isOpponentRequired && !isEventVenueSelected(values.venue)) {
    actions.push({
      id: 'event-details-venue',
      label: 'Choose a venue',
      buttonLabel: 'Choose venue to continue',
      description: 'Select an existing or custom venue, a postcode or To be decided.'
    });
  }

  return actions;
};

const EventContinueActions: React.FunctionComponent<{
  values: any;
  venue: any;
  onCancel: () => void;
  onContinue: () => void;
}> = ({ values, venue, onCancel, onContinue }) => {
  const requiredActions = getEventDetailsRequiredActions({ ...values, venue: values.venue || venue });
  const firstRequiredAction = requiredActions[0];
  const isReady = requiredActions.length === 0;
  const isTeamSport = propz.get(values, ['sport', 'players'], TYPE_OF_PLAYERS.TEAM) === TYPE_OF_PLAYERS.TEAM;
  const selectedAgeCount = (values.ages || []).filter(item => item.isChecked).length;

  return (
    <div className="eSchoolEventForm__actions" data-help-anchor="event-details-actions">
      <div className="eSchoolEventForm__actionsInfo">
        <div className="eSchoolEventForm__actionsTitle">Step 1 of 2 — Event details</div>
        <div className="eSchoolEventForm__actionsText">
          {isReady
            ? `${isTeamSport ? 'Ready to choose or build the team.' : 'Ready to choose participants.'} ${
                selectedAgeCount > 0
                  ? `${selectedAgeCount} age ${selectedAgeCount === 1 ? 'group is' : 'groups are'} selected.`
                  : 'No age restriction is applied, so all ages will be eligible.'
              }`
            : `${firstRequiredAction.description}${
                requiredActions.length > 1
                  ? ` ${requiredActions.length - 1} more required ${
                      requiredActions.length === 2 ? 'item remains' : 'items remain'
                    }.`
                  : ''
              }`}
        </div>
      </div>

      <div className="eSchoolEventForm__actionsButtons">
        <button type="button" onClick={onCancel} className="eSchoolEventForm__btnCancel">
          Cancel
        </button>
        <div className="eSchoolEventForm__continueGroup">
          {!isReady ? (
            <div className="eSchoolEventForm__disabledReason" role="status">
              {firstRequiredAction.label}. {firstRequiredAction.description}
            </div>
          ) : null}
          <button
            type="button"
            onClick={onContinue}
            className="btn btn-primary eSchoolEventForm__btnPrimary eSchoolEventForm__btnNext"
            disabled={!isReady}
          >
            <span>
              {isReady
                ? isTeamSport
                  ? 'Continue → Team members'
                  : 'Continue → Participants'
                : firstRequiredAction.buttonLabel}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const EventDetailsPageHelp: React.FunctionComponent<EventDetailsPageHelpProps> = props => {
  const {
    values,
    venue,
    user,
    isEditing,
    isShowAllSports,
    isActivitySourceToggleVisible,
    isShortDescriptionVisible,
    isEventTypeLocked
  } = props;
  const registrationRef = React.useRef<PageHelpRegistration | null>(null);
  const defaultFixtureStartTime = propz.get(user, ['activeSchool', 'defaultFixtureStartTime'], '10:00') || '10:00';
  const activityName = propz.get(values, ['sport', 'name'], '');
  const isActivitySelected = Boolean(values.sportId);
  const isMultipleTeamsUnavailable = !values.sportId;
  const isAudienceUnavailable = !isActivitySelected;
  const isVenueUnavailable =
    values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && (values.participants || []).length === 0;
  const selectedGenders = (values.gender || []).filter(item => item.isChecked).map(item => item.name);
  const selectedAges = (values.ages || []).filter(item => item.isChecked);
  const eventTypeLabel = EVENT_TYPES_SERVER_TO_CLIENT_MAPPING[values.eventType] || 'Not selected';
  const participantCount =
    values.eventType === EVENT_TYPES.INTERNAL_HOUSES
      ? (values.houses || []).length
      : values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS
      ? (values.participants || []).length
      : 0;
  const startTime = moment(values.startTime);
  const endTime = moment(values.endTime);
  const isTimeRangeValid = startTime.isValid() && endTime.isValid() && !startTime.isAfter(endTime);
  const requiredActions = getEventDetailsRequiredActions({ ...values, venue: values.venue || venue });

  const firstRequiredAction = requiredActions[0];
  const isReadyToContinue = requiredActions.length === 0;
  const firstRequiredActionId = firstRequiredAction ? firstRequiredAction.id : 'event-details-actions';
  const activitySearchMode = isActivitySourceToggleVisible
    ? isShowAllSports
      ? 'All activities'
      : 'Favourites'
    : 'School activity catalogue';
  const participantAnchor =
    values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS
      ? {
          label: 'Opponent schools',
          description:
            participantCount === 0
              ? 'No opponent is selected. Search by school name or postcode; Venue unlocks after the first selection.'
              : `${participantCount} opponent ${
                  participantCount === 1 ? 'school is' : 'schools are'
                } selected. Schools are ordered by proximity when your school has a postcode; use the radius to narrow the search.`
        }
      : values.eventType === EVENT_TYPES.INTERNAL_HOUSES
      ? {
          label: 'Participating houses',
          description:
            participantCount < 2
              ? `${participantCount} selected. Choose at least two houses; use Multiple teams when more than two will take part.`
              : `${participantCount} houses selected. ${
                  values.multiparty
                    ? 'Multiple teams allows additional houses up to the configured event limit.'
                    : 'This is ready as a head-to-head house event.'
                }`
        }
      : {
          label: 'Own-school participants',
          description: 'This event uses your own students. Eligible team members or individuals are selected next.'
        };
  const anchors = [
    {
      id: 'event-details-time',
      label: 'Date and times',
      description: isTimeRangeValid
        ? 'The time range is valid. Start time comes from Admin console → School settings; finish remains editable.'
        : 'The time range is not valid. Finish must be the same as or later than start.'
    },
    ...(isShortDescriptionVisible
      ? [
          {
            id: 'event-details-description',
            label: 'Short description',
            description:
              'Optional. Enter the name people should see, or leave it blank for Squad In Touch to generate one from the event details.'
          }
        ]
      : []),
    {
      id: 'event-details-activity',
      label: 'Activity / Sport',
      description: isActivitySelected
        ? isActivitySourceToggleVisible
          ? `${activityName} is selected. You can now set Multiple teams, gender and age groups. Favourites are your school’s shortlist; manage them in Admin console → Activities & Sports using the star in each row or the bulk Add to favourites action.`
          : `${activityName} is selected. You can now set Multiple teams, gender and age groups. This school is using its configured activity catalogue; favourite-management controls are not currently available here.`
        : isActivitySourceToggleVisible
        ? 'Choose this first. Multiple teams, gender and age groups remain unavailable until an Activity / Sport is selected. Favourites are your school’s shortlist of commonly used activities. Manage them in Admin console → Activities & Sports using the stars, or switch to All activities here for the full catalogue.'
        : 'Choose this first. Multiple teams, gender and age groups remain unavailable until an Activity / Sport is selected. This school is using its configured activity catalogue; favourite-management controls are not currently available. Contact support if an expected activity is missing.'
    },
    {
      id: 'event-details-multiple-teams',
      label: 'Multiple teams',
      description: isMultipleTeamsUnavailable
        ? 'Currently unavailable because no Activity / Sport has been selected. Choose one first, then you can decide whether this event needs multiple teams.'
        : 'Available now. This allows several school/opponent teams or houses and is intended for points or placings rather than individual scorelines.'
    },
    {
      id: 'event-details-gender',
      label: 'Gender',
      description: isAudienceUnavailable
        ? 'Currently unavailable because no Activity / Sport has been selected. Choose one first; its supported gender options will then become available.'
        : 'Available now. Select both boys and girls for a mixed event. This choice narrows eligible participants next.'
    },
    {
      id: 'event-details-ages',
      label: 'Age groups',
      description: isAudienceUnavailable
        ? 'Currently unavailable because no Activity / Sport has been selected. Choose one first, then select age groups or leave them all off to allow every age.'
        : 'Available now. Leave every age off to allow all ages, or select the groups that should be available on the next step.'
    },
    {
      id: 'event-details-type',
      label: 'Event type',
      description: isEventTypeLocked
        ? `${eventTypeLabel} is fixed because this event is being created from a competition or league.`
        : `${eventTypeLabel} is selected. Inter-schools involves another school, House uses school houses and Internal uses your own teams or individuals.`
    },
    {
      id: 'event-details-participants',
      label: participantAnchor.label,
      description: participantAnchor.description
    },
    {
      id: 'event-details-venue',
      label: 'Venue',
      description: isVenueUnavailable
        ? 'Currently unavailable because this is an Inter-schools event with no opponent school selected. Choose an opponent first; the venue field will then include that school’s locations as well as existing/custom venues and postcodes.'
        : 'Available now. Choose your school, an opponent venue, an existing/custom venue or a postcode. Check the map point so families can find it.'
    },
    {
      id: 'event-details-contact',
      label: 'Reply-to email',
      description:
        'Replies to event emails use this address, then the school notification address, then the Squad In Touch system address.'
    },
    {
      id: 'event-details-actions',
      label: 'Continue',
      description: !isReadyToContinue
        ? `Continue is disabled because ${requiredActions.length} required ${
            requiredActions.length === 1 ? 'item is' : 'items are'
          } still incomplete. First: ${firstRequiredAction ? firstRequiredAction.label : 'review the required fields'}.`
        : propz.get(values, ['sport', 'players'], TYPE_OF_PLAYERS.TEAM) === TYPE_OF_PLAYERS.TEAM
        ? `Continue is available. It takes you to the team-members step for ${activityName || 'the selected activity'}.`
        : `Continue is available. It takes you to the participant-selection step for ${activityName ||
            'the selected activity'}.`
    }
  ];

  const definition: PageHelpDefinition = {
    screenId: isEditing ? 'school.events.edit.details' : 'school.events.create.details',
    title: isEditing ? 'Edit event details' : 'Create an event — details',
    subtitle: 'Step 1: define when and where the event happens, who it is for and how it should be organised.',
    entity: { singular: 'event', plural: 'events' },
    useDefaultSelectionObservation: false,
    observations: [
      {
        id: 'event-required-actions',
        tone: isReadyToContinue ? 'positive' : 'warning',
        title: isReadyToContinue
          ? 'Ready to continue'
          : `${requiredActions.length} required ${requiredActions.length === 1 ? 'action' : 'actions'} remaining`,
        text: isReadyToContinue
          ? propz.get(values, ['sport', 'players'], TYPE_OF_PLAYERS.TEAM) === TYPE_OF_PLAYERS.TEAM
            ? 'Continue will open the team-members step.'
            : 'Continue will open individual participant selection.'
          : requiredActions.map(item => item.label).join(' · ')
      },
      {
        id: 'event-time-summary',
        tone: isTimeRangeValid ? 'neutral' : 'warning',
        title: `${getEventHelpTime(values.startTime)}–${getEventHelpTime(values.endTime)}`,
        text: isTimeRangeValid
          ? `New fixtures start at ${defaultFixtureStartTime} by default, as configured in Admin console → School settings. This event’s finish can be adjusted independently after the start time is set.`
          : 'Finish is earlier than start. Correct the time range before continuing.'
      },
      {
        id: 'event-activity-summary',
        tone: activityName ? 'positive' : 'warning',
        title: activityName || 'Choose an Activity / Sport',
        text: activityName
          ? isActivitySourceToggleVisible
            ? `${activitySearchMode} is the current search source${
                values.multiparty ? ', with Multiple teams enabled' : ''
              }. Favourites are the school’s quick-access shortlist and can be changed in Admin console → Activities & Sports.`
            : `${activitySearchMode} is the current search source${
                values.multiparty ? ', with Multiple teams enabled' : ''
              }. Favourite-management controls are not available for this school configuration.`
          : isActivitySourceToggleVisible
          ? `Search Favourites first. This is the school’s shortlist of commonly used activities, managed in Admin console → Activities & Sports. Switch to All activities if the activity is not there.`
          : `Search your school’s configured activity catalogue. The Favourites / All activities switch is unavailable for this school configuration; contact support if an expected activity is missing.`
      },
      {
        id: 'event-audience-summary',
        tone: isAudienceUnavailable || selectedGenders.length === 0 ? 'warning' : 'neutral',
        title: isAudienceUnavailable
          ? 'Audience options are locked'
          : `Audience: ${selectedGenders.length > 0 ? selectedGenders.join(' + ') : 'gender not selected'}`,
        text: isAudienceUnavailable
          ? 'Choose an Activity / Sport to unlock Multiple teams, gender and age groups. These settings depend on the selected activity.'
          : selectedAges.length === 0
          ? 'No age groups are selected, so every age remains eligible for the participant step.'
          : `${selectedAges.length} age ${
              selectedAges.length === 1 ? 'group is' : 'groups are'
            } selected; only matching children will be available next.`
      },
      {
        id: 'event-type-summary',
        tone: isVenueUnavailable ? 'warning' : 'neutral',
        title: `${eventTypeLabel}${isEventTypeLocked ? ' · fixed by the source competition' : ''}`,
        text:
          values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS
            ? isVenueUnavailable
              ? 'No opponent school is selected, so Venue is currently unavailable. Choose an opponent first; nearby schools appear first and the distance setting can narrow the results.'
              : `${participantCount} opponent ${
                  participantCount === 1 ? 'school' : 'schools'
                } selected. Venue is available; nearby schools appear first and the distance setting can narrow the results.`
            : values.eventType === EVENT_TYPES.INTERNAL_HOUSES
            ? `${participantCount} ${participantCount === 1 ? 'house' : 'houses'} selected for this house event.`
            : 'This internal event uses participants from your own school; you will choose them on the next step.'
      }
    ],
    anchors,
    actions: [
      {
        id: 'event-help-activity',
        label: activityName ? 'Review Activity / Sport' : 'Choose Activity / Sport',
        description: 'Go to the activity search and Multiple teams setting.',
        kind: activityName ? 'secondary' : 'primary',
        anchorId: 'event-details-activity',
        run: () => scrollToEventHelpAnchor('event-details-activity')
      },
      {
        id: 'event-help-audience',
        label: isAudienceUnavailable ? 'Why audience options are locked' : 'Check audience',
        description: isAudienceUnavailable
          ? 'Go to the locked gender and age controls; choose an Activity / Sport to enable them.'
          : 'Review gender and age eligibility before continuing.',
        kind: 'secondary',
        anchorId: 'event-details-gender',
        run: () => scrollToEventHelpAnchor('event-details-gender')
      },
      {
        id: 'event-help-venue',
        label: isVenueUnavailable ? 'Why Venue is locked' : 'Check venue & contact',
        description: isVenueUnavailable
          ? 'Go to Venue; select an opponent school first to enable it.'
          : 'Confirm the location, map point and reply-to address.',
        kind: 'secondary',
        anchorId: 'event-details-venue',
        run: () => scrollToEventHelpAnchor('event-details-venue')
      },
      {
        id: 'event-help-continue',
        label: isReadyToContinue
          ? 'Continue to participants'
          : firstRequiredAction
          ? firstRequiredAction.label
          : 'What is needed before Continue?',
        description: isReadyToContinue
          ? propz.get(values, ['sport', 'players'], TYPE_OF_PLAYERS.TEAM) === TYPE_OF_PLAYERS.TEAM
            ? `The selected activity is a team sport, so the next step builds or selects the team.`
            : `The selected activity is individual, so the next step selects eligible participants.`
          : firstRequiredAction
          ? firstRequiredAction.description
          : 'Complete the required event details first.',
        kind: 'primary',
        anchorId: isReadyToContinue ? 'event-details-actions' : firstRequiredActionId,
        run: () => scrollToEventHelpAnchor(isReadyToContinue ? 'event-details-actions' : firstRequiredActionId)
      }
    ],
    sections: [
      {
        id: 'event-help-actions-now',
        title: 'Available actions now',
        items: [
          isReadyToContinue
            ? 'Continue is ready and opens the correct team or participant step for the selected activity.'
            : `Continue is disabled and will not advance until you complete: ${requiredActions
                .map(item => item.label)
                .join(', ')}.`,
          isVenueUnavailable
            ? 'Venue and Create venue are currently disabled. Choose an opponent school to enable them.'
            : 'Venue search and Create venue are available.',
          isEventTypeLocked
            ? 'Event type cannot be changed because it is controlled by the source competition or league.'
            : 'Event type can be changed; changing it also changes the participant and venue controls.',
          'Cancel leaves event creation. Details entered in this unsaved flow are not stored.'
        ],
        anchorIds: ['event-details-actions']
      },
      {
        id: 'event-help-basics',
        title: 'Time, description and activity',
        items: [
          `The default fixture start time is ${defaultFixtureStartTime}; change it in Admin console → School settings when the school-wide default needs updating.`,
          'Finish starts one hour after the selected start time and can be changed manually.',
          'Short description is optional; when blank, the platform builds an event name from the sport, gender, ages and other event data.',
          isActivitySourceToggleVisible
            ? 'Favourites are the school’s quick-access shortlist of commonly used activities. Open Admin console → Activities & Sports and use a row’s star, or select several rows and choose Add to favourites / Remove from favourites.'
            : 'Favourite-management controls are not enabled for this school. The configured activity catalogue is searched instead; contact Squad In Touch support if an expected activity is missing.',
          isActivitySourceToggleVisible
            ? 'Use All activities when an item is missing from Favourites. If it is also missing from the full catalogue, contact Squad In Touch support.'
            : 'If an activity is missing from the configured catalogue, contact Squad In Touch support so we can check the school setup.'
        ]
      },
      {
        id: 'event-help-audience-type',
        title: 'Audience, event type and participants',
        items: [
          isAudienceUnavailable
            ? 'Multiple teams, gender and age groups are currently unavailable because no Activity / Sport has been selected. Choose one first to unlock them.'
            : 'Select boys and girls together for a mixed event. Leave every age group off to include all ages.',
          'Gender and ages determine which children are available on the next participant/team step.',
          'Inter-schools is the default; House is for house competition and Internal is for your own school teams or individuals.',
          'Multiple teams is useful for tournament-style events recorded with points or placings instead of separate scorelines.'
        ]
      },
      {
        id: 'event-help-location-contact',
        title: 'Venue and replies',
        items: [
          'Nearby opponent schools are returned first when your school postcode is configured; the distance selector limits the radius.',
          isVenueUnavailable
            ? 'Venue is currently unavailable because an Inter-schools event needs an opponent school first. Choose the opponent, then select its venue, another existing/custom venue or a postcode.'
            : 'Use an existing venue, an opponent venue, a custom venue or postcode, and verify the map marker before saving.',
          'The reply-to field controls where replies to event emails go. When blank, the school notification address and then the platform address are used.'
        ]
      }
    ]
  };

  React.useEffect(() => {
    registrationRef.current = registerPageHelpDefinition(definition);
    return () => {
      if (registrationRef.current) {
        registrationRef.current.unregister();
      }
      registrationRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    if (registrationRef.current) {
      registrationRef.current.update(definition);
    }
  });

  return null;
};

const AGE_KEY_STAGES = [
  { label: 'EYFS', names: ['Reception'] },
  { label: 'KS1', names: ['Y1', 'Y2'] },
  { label: 'KS2', names: ['Y3', 'Y4', 'Y5', 'Y6'] },
  { label: 'KS3', names: ['Y7', 'Y8', 'Y9'] },
  { label: 'KS4', names: ['Y10', 'Y11'] },
  { label: 'KS5', names: ['Y12', 'Y13'] }
];

function getAgeKeyStageGroups(ages: any[]) {
  const indexedAges = (ages || []).map((item, index) => ({ item, index }));
  const assigned = new Set<number>();
  const groups = AGE_KEY_STAGES.map(stage => {
    const items = indexedAges.filter(({ item, index }) => {
      const name = String(item.name || '').replace(/\s+/g, '');
      const isMatch = stage.names.some(stageName => stageName.toLowerCase() === name.toLowerCase());
      if (isMatch) assigned.add(index);
      return isMatch;
    });
    return { label: stage.label, items };
  }).filter(group => group.items.length > 0);
  const otherItems = indexedAges.filter(({ index }) => !assigned.has(index));
  return otherItems.length > 0 ? [...groups, { label: 'Other', items: otherItems }] : groups;
}

interface State {
  participants: any[];
  point: Point;
  venue: any;
  allHouses: SchoolHouse[];
  houses: any[];
  eventType: string;
  distance: number;
  isDeleteVenueModalOpen: boolean;
  isCreateVenueFormOpen: boolean;
  isShowAllSports: boolean;
  multiparty: boolean;
  isSelectAllHouses: boolean;
  ageSearch: string;
  isAgeDropdownOpen: boolean;
  activityInputText: string;
}

export class SchoolEventForm extends React.Component<Props, State> {
  private _isMounted: boolean = false;
  private ageDropdownRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    const { schoolEvent, user } = this.props;
    const venue = propz.get(schoolEvent, ['venue']);
    const convertPlace = this.isExistEvent() ? convertPlaceToPostcodeWithPlaceName(venue) : undefined;

    this.state = {
      allHouses: [],
      houses: this.isExistEvent() ? propz.get(schoolEvent, ['houses'], []) : [],
      participants: this.isExistEvent() ? propz.get(schoolEvent, ['participants'], []) : [],
      point: propz.get(convertPlace, ['point'], getDefaultPoint(user)),
      distance: VENUE_DISTANCE.UNLIMITED,
      eventType: this.setEventType(),
      venue: this.isExistEvent() ? schoolEvent.venue : undefined,
      isDeleteVenueModalOpen: false,
      isCreateVenueFormOpen: false,
      isShowAllSports: false,
      multiparty: this.isExistEvent() ? propz.get(schoolEvent, ['multiparty'], false) : false,
      isSelectAllHouses: false,
      ageSearch: '',
      isAgeDropdownOpen: false,
      activityInputText: this.isExistEvent() ? propz.get(schoolEvent, ['sport', 'name'], '') : ''
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const { user } = this.props;
    getAllHouses(user).then(allHouses => {
      if (this._isMounted) {
        this.setState({
          allHouses
        });
      }
    });

    document.addEventListener('mousedown', this.onDocumentMouseDown);
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  applyDatePart = (value: Date, datePart: Date): Date => {
    return new Date(
      datePart.getFullYear(),
      datePart.getMonth(),
      datePart.getDate(),
      value.getHours(),
      value.getMinutes(),
      0,
      0
    );
  };

  applyTimePart = (value: Date, timePart: Date): Date => {
    return new Date(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
      timePart.getHours(),
      timePart.getMinutes(),
      0,
      0
    );
  };

  addOneHour = (value: Date): Date => {
    const nextValue = new Date(value.getTime());
    nextValue.setHours(nextValue.getHours() + 1, nextValue.getMinutes(), 0, 0);
    return nextValue;
  };

  renderUsersIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );

  renderFlagIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <path d="M4 22v-7" />
    </svg>
  );

  renderHomeIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
  renderShieldIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );

  renderPinIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s7-6.13 7-12a7 7 0 1 0-14 0c0 5.87 7 12 7 12z" />
      <path d="M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
    </svg>
  );

  onDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target as Node | null;

    if (this.state.isAgeDropdownOpen) {
      const node = this.ageDropdownRef.current;
      if (node && target && !node.contains(target)) {
        this.closeAgeDropdown();
      }
    }
  };

  setEventType() {
    const { schoolEvent, aggregationType } = this.props;
    const isAggregationTypeExist = typeof aggregationType !== 'undefined' && aggregationType !== '';

    if (isAggregationTypeExist) {
      return AGGREGATION_AND_EVENT_TYPES_MAPPING[aggregationType];
    }

    return propz.get(schoolEvent, ['eventType'], EVENT_TYPES.EXTERNAL_SCHOOLS);
  }

  getEventSchema() {
    const { eventType, multiparty, venue } = this.state;

    let schema;

    switch (true) {
      case eventType === EVENT_TYPES.EXTERNAL_SCHOOLS:
        schema = Yup.object().shape({
          startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
            const { endTime } = this.parent;
            if (!value || !endTime) return true;
            return value <= endTime;
          }),
          endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
            const { startTime } = this.parent;
            if (!value || !startTime) return true;
            return value >= startTime;
          }),
          sportId: Yup.string().required('Select an Activity / Sport from the suggestions'),
          gender: Yup.array().test('gender', 'Required', function(value) {
            const genderItemsChecked = value.filter(genderItem => genderItem.isChecked);
            return genderItemsChecked.length > 0;
          }),
          eventType: Yup.string().required('Required'),
          participants: Yup.mixed(),
          multiparty: Yup.boolean(),
          leaguePositionsCount: Yup.number()
        });
        break;

      case eventType === EVENT_TYPES.INTERNAL_HOUSES && !multiparty:
        schema = Yup.object().shape({
          startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
            const { endTime } = this.parent;
            if (!value || !endTime) return true;
            return value <= endTime;
          }),
          endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
            const { startTime } = this.parent;
            if (!value || !startTime) return true;
            return value >= startTime;
          }),
          sportId: Yup.string().required('Select an Activity / Sport from the suggestions'),
          gender: Yup.array().test('gender', 'Required', function(value) {
            const genderItemsChecked = value.filter(genderItem => genderItem.isChecked);
            return genderItemsChecked.length > 0;
          }),
          eventType: Yup.string().required('Required'),
          houses: Yup.array()
            .min(2)
            .max(2),
          multiparty: Yup.boolean(),
          leaguePositionsCount: Yup.number()
        });
        break;

      case eventType === EVENT_TYPES.INTERNAL_HOUSES && multiparty:
        schema = Yup.object().shape({
          startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
            const { endTime } = this.parent;
            if (!value || !endTime) return true;
            return value <= endTime;
          }),
          endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
            const { startTime } = this.parent;
            if (!value || !startTime) return true;
            return value >= startTime;
          }),
          sportId: Yup.string().required('Select an Activity / Sport from the suggestions'),
          gender: Yup.array().test('gender', 'Required', function(value) {
            const genderItemsChecked = value.filter(genderItem => genderItem.isChecked);
            return genderItemsChecked.length > 0;
          }),
          eventType: Yup.string().required('Required'),
          houses: Yup.array().min(2),
          multiparty: Yup.boolean(),
          leaguePositionsCount: Yup.number()
        });
        break;

      case eventType === EVENT_TYPES.INTERNAL_TEAMS:
        schema = Yup.object().shape({
          startTime: Yup.date().test('startTime', 'Must be earlier than end time', function(value) {
            const { endTime } = this.parent;
            if (!value || !endTime) return true;
            return value <= endTime;
          }),
          endTime: Yup.date().test('endTime', 'Must be later than start time', function(value) {
            const { startTime } = this.parent;
            if (!value || !startTime) return true;
            return value >= startTime;
          }),
          sportId: Yup.string().required('Select an Activity / Sport from the suggestions'),
          gender: Yup.array().test('gender', 'Required', function(value) {
            const genderItemsChecked = value.filter(genderItem => genderItem.isChecked);
            return genderItemsChecked.length > 0;
          }),
          eventType: Yup.string().required('Required'),
          venue: Yup.mixed().required('Required'),
          multiparty: Yup.boolean(),
          leaguePositionsCount: Yup.number()
        });
        break;
    }

    return schema;
  }

  isExistEvent() {
    return typeof this.props.schoolEvent !== 'undefined';
  }

  getSports = (text: string, setFieldValue) => {
    const { isShowAllSports } = this.state;
    const { user, aggregationType } = this.props;

    const isAggregationTypeExist = typeof aggregationType !== 'undefined' && aggregationType !== '';
    const canEditFavoriteSports = propz.get(user, ['activeSchool', 'canEditFavoriteSports']);
    const isFavoriteSportsEnabled = propz.get(user, ['activeSchool', 'isFavoriteSportsEnabled']);
    const isOnlyFavoriteSports = isFavoriteSportsEnabled ? canEditFavoriteSports && !isShowAllSports : true;

    if (isAggregationTypeExist) {
      if (text === '') {
        setFieldValue('sportId', '');
        setFieldValue('sport', undefined);
      }

      switch (aggregationType) {
        case LEAGUE_AGGREGATION_TYPE.HOUSE:
        case LEAGUE_AGGREGATION_TYPE.TEAM:
          return searchFunctionTeamSports(user, text, isOnlyFavoriteSports);

        case LEAGUE_AGGREGATION_TYPE.INDIVIDUAL:
          return searchFunctionIndividualSports(user, text, isOnlyFavoriteSports);
      }
    }

    if (text === '') {
      setFieldValue('sportId', '');
      setFieldValue('sport', undefined);
    }

    return searchFunctionSportsWithFavorite(user, text, isOnlyFavoriteSports);
  };

  isExistSport = sport => {
    return typeof sport !== 'undefined';
  };

  getNewPoint = pointNew => {
    this.setState({
      point: pointNew
    });
  };

  onGenderChange = (index, setFieldValue, values) => {
    const genderItemsUpdated = values.gender.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    setFieldValue('gender', genderItemsUpdated);
  };

  onAgesChange = (index, setFieldValue, values) => {
    const ageItemsUpdated = values.ages.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    setFieldValue('ages', ageItemsUpdated);
  };

  getSelectedAgesLabel = values => {
    const { user, schoolEvent } = this.props;
    const naming = resolveAgeNamingFromContext(user, schoolEvent);

    const selectedAges = (values.ages || [])
      .filter(item => item.isChecked)
      .map(item => Number(propz.get(item, ['age'], item.key)))
      .filter(age => Number.isFinite(age));

    if (selectedAges.length === 0) return 'Select ages';

    return formatAgesCompact(selectedAges, naming);
  };

  toggleAgeDropdown = () => {
    this.setState(prev => ({
      isAgeDropdownOpen: !prev.isAgeDropdownOpen
    }));
  };

  closeAgeDropdown = () => {
    this.setState({
      isAgeDropdownOpen: false,
      ageSearch: ''
    });
  };

  onAgeSearchChange = event => {
    this.setState({
      ageSearch: event.target.value
    });
  };

  onSelectAllAges = (setFieldValue, values) => {
    const next = (values.ages || []).map(item => ({
      ...item,
      isChecked: true
    }));

    setFieldValue('ages', next);
  };

  onClearAllAges = (setFieldValue, values) => {
    const next = (values.ages || []).map(item => ({
      ...item,
      isChecked: false
    }));

    setFieldValue('ages', next);
  };

  onSelectFilteredAges = (setFieldValue, values) => {
    const query = String(this.state.ageSearch || '')
      .trim()
      .toLowerCase();

    const next = (values.ages || []).map(item => {
      const matches =
        query === '' ||
        String(item.name || '')
          .toLowerCase()
          .includes(query);

      return matches ? { ...item, isChecked: true } : item;
    });

    setFieldValue('ages', next);
  };

  onCancelCreateVenueFormOpenClick = (): void => {
    this.setState({
      isCreateVenueFormOpen: false
    });
  };

  createVenueFormOpen = (): void => {
    this.setState({
      isCreateVenueFormOpen: true
    });
  };

  onSubmitVenueForm = (data): void => {
    const { user } = this.props;

    createVenue(user, data).then(venue => {
      const convertPlace = convertPlaceToPostcodeWithPlaceName(venue);

      this.setState({
        venue: convertPlace,
        point: convertPlace.point,
        isCreateVenueFormOpen: false
      });
    });
  };

  renderCreateVenueForm() {
    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={this.state.isCreateVenueFormOpen}
        title="New venue"
        onCloseClick={this.onCancelCreateVenueFormOpenClick}
        customClass="modal-md"
        showFooter={false}
        dialogStyle={{ maxWidth: 680 }}
      >
        <VenueForm user={user} onCancel={this.onCancelCreateVenueFormOpenClick} onSubmit={this.onSubmitVenueForm} />
      </SimpleModal>
    );
  }

  renderParticipants(participants, setFieldValue) {
    return participants.map(participant => {
      const imageSrc =
        participant.pic && String(participant.pic).startsWith('//') ? `https:${participant.pic}` : participant.pic;

      return (
        <div key={participant.id} className="eEntityChipWrap">
          <div className="eEntityChip">
            <div className="eEntityChip__avatar">
              {imageSrc ? (
                <img src={imageSrc} alt={participant.name} className="eEntityChip__avatarImg" />
              ) : (
                <div className="eEntityChip__avatarFallback">
                  {String(participant.name || '?')
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
            </div>

            <div className="eEntityChip__name" title={participant.name}>
              {participant.name}
            </div>

            <button
              type="button"
              className="eEntityChip__remove"
              onClick={() => {
                const participantsFiltered = participants.filter(p => p.id !== participant.id);
                setFieldValue('participants', participantsFiltered);
                this.setState({
                  participants: participantsFiltered
                });
              }}
            >
              ×
            </button>
          </div>
        </div>
      );
    });
  }

  renderHouses(houses, setFieldValue) {
    return houses.map(house => {
      const imageSrc = house.pic;
      const chipStyle =
        !imageSrc && Array.isArray(house.colors) && house.colors.length >= 2
          ? {
              background: `linear-gradient(135deg, ${house.colors[0]} 0%, ${house.colors[1]} 100%)`
            }
          : undefined;

      return (
        <div key={house.id} className="eEntityChipWrap">
          <div className="eEntityChip">
            <div className="eEntityChip__avatar">
              {imageSrc ? (
                <img src={imageSrc} alt={house.name} className="eEntityChip__avatarImg" />
              ) : (
                <div className="eEntityChip__avatarFallback" style={chipStyle}>
                  {String(house.name || '?')
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}
            </div>

            <div className="eEntityChip__name" title={house.name}>
              {house.name}
            </div>

            <button
              type="button"
              className="eEntityChip__remove"
              onClick={() => {
                const housesFiltered = houses.filter(item => item.id !== house.id);
                const { allHouses } = this.state;
                setFieldValue('houses', housesFiltered);
                this.setState({
                  houses: housesFiltered,
                  isSelectAllHouses: housesFiltered.length === allHouses.length
                });
              }}
            >
              ×
            </button>
          </div>
        </div>
      );
    });
  }

  getSchools = (text: string) => {
    const { user } = this.props;
    const { participants, distance } = this.state;

    return searchFunctionEventParticipants(text, user, participants, distance);
  };

  getHouses = (text?: string) => {
    const { user } = this.props;
    const { houses } = this.state;
    const houseIds = houses.map(house => house.id);

    return searchFunctionAllHousesNinHouseIds(user, text, houseIds);
  };

  onIsShowAllSportsChange = (event, setFieldValue, values) => {
    const isShowAllSports = event.target.checked;
    const isActiveSportFavorite = propz.get(values, ['sport', 'isFavorite'], false);

    if (!isShowAllSports && !isActiveSportFavorite) {
      setFieldValue('sportId', '');
      setFieldValue('sport', undefined);
    }

    this.setState({ isShowAllSports });
  };

  onMultipartyChange = (event, setFieldValue, values) => {
    const multiparty = event.target.checked;
    const participants = multiparty ? values.participants : values.participants.slice(0, 1);
    const houses = multiparty ? values.houses : values.houses.slice(0, 2);

    setFieldValue('multiparty', multiparty);
    setFieldValue('participants', participants);
    setFieldValue('houses', houses);

    this.setState({
      multiparty,
      isSelectAllHouses: multiparty && houses.length === this.state.allHouses.length,
      houses,
      participants
    });
  };

  onAllHousesClick = (setFieldValue: (field: string, data: any) => void) => {
    const { allHouses, isSelectAllHouses, multiparty } = this.state;

    if (!multiparty) {
      return;
    }

    if (isSelectAllHouses) {
      this.setState({
        houses: [],
        isSelectAllHouses: false
      });
      setFieldValue('houses', []);
    } else {
      this.setState({
        houses: allHouses,
        isSelectAllHouses: true
      });
      setFieldValue('houses', allHouses);
    }
  };

  onAddExistingTeamClick = (setFieldValue, submitForm) => {
    setFieldValue('addTeamOption', EVENT_ADD_TEAM.EXISTING);
    submitForm();
  };

  onAddNewTeamClick = (setFieldValue, submitForm) => {
    setFieldValue('addTeamOption', EVENT_ADD_TEAM.NEW);
    submitForm();
  };

  render() {
    const {
      participants,
      point,
      venue,
      houses,
      eventType,
      distance,
      isShowAllSports,
      multiparty,
      isSelectAllHouses
    } = this.state;

    const { schoolEvent, user, onSubmit, forms, createDate, aggregationType } = this.props;
    const { activeSchool } = user;

    const isSchoolHaveFavoriteSports = propz.get(user, ['activeSchool', 'canEditFavoriteSports']);
    const isFavoriteSportsEnabled = propz.get(user, ['activeSchool', 'isFavoriteSportsEnabled']);
    const defaultFixtureStartTime = propz.get(user, ['activeSchool', 'defaultFixtureStartTime']);

    const isExistActiveSchoolPostcode = propz.get(user, ['activeSchool', 'postcode'], false);
    const isExistCreateDate = typeof createDate !== 'undefined';
    const isDefaultFixtureStartTimeExist =
      typeof defaultFixtureStartTime !== 'undefined' && defaultFixtureStartTime !== '';
    const isAggregationTypeExist = typeof aggregationType !== 'undefined' && aggregationType !== '';

    let startTime;
    let endTime;

    switch (true) {
      case this.isExistEvent():
        startTime = schoolEvent.startTime;
        endTime = schoolEvent.endTime;
        break;

      case isExistCreateDate:
        const startTimeDateObj = moment(createDate, ' DD-MM-YYYY').toDate();

        if (isDefaultFixtureStartTimeExist) {
          const timeParts = defaultFixtureStartTime.split(':');
          startTimeDateObj.setHours(Number(timeParts[0]), Number(timeParts[1]), 0, 0);
        } else {
          startTimeDateObj.setHours(10, 0);
        }

        startTime = startTimeDateObj;

        const endTimeDateObj = new Date(startTimeDateObj.getTime());
        endTimeDateObj.setHours(startTimeDateObj.getHours() + 1, startTimeDateObj.getMinutes());
        endTime = endTimeDateObj;
        break;

      default:
        const startTimeCurrentDate = new Date();

        if (isDefaultFixtureStartTimeExist) {
          const timeParts = defaultFixtureStartTime.split(':');
          startTimeCurrentDate.setHours(Number(timeParts[0]), Number(timeParts[1]), 0, 0);
        } else {
          startTimeCurrentDate.setHours(10, 0);
        }

        startTime = startTimeCurrentDate;

        const endTimeCurrentDate = new Date();
        endTimeCurrentDate.setHours(startTimeCurrentDate.getHours() + 1, startTimeCurrentDate.getMinutes());
        endTime = endTimeCurrentDate;
        break;
    }

    const eventInitial = {
      startTime,
      endTime,
      sport: this.isExistEvent() ? schoolEvent.sport : undefined,
      sportId: this.isExistEvent() ? schoolEvent.sportId : '',
      ages: this.isExistEvent()
        ? getAgesForMultiSelect(activeSchool, forms, schoolEvent.ages)
        : getAgesForMultiSelect(activeSchool, forms),
      gender: this.isExistEvent()
        ? getGendersForMultiSelect(schoolEvent.sport, schoolEvent.gender)
        : getDefaultGendersForMultiSelect(),
      eventType,
      participants,
      venue,
      houses,
      distance,
      multiparty: this.isExistEvent() ? schoolEvent.multiparty : false,
      isSelectAllHouses,
      addTeamOption: '',
      shortDescription: this.isExistEvent() ? propz.get(schoolEvent, ['shortDescription'], '') : '',
      picUrl: this.isExistEvent() ? propz.get(schoolEvent, ['photos', 0, 'picUrl'], '') : '',
      isStudentCanSubmitResult: this.isExistEvent() ? schoolEvent.isStudentCanSubmitResult : false,
      isStudentCanJoin: this.isExistEvent() ? schoolEvent.isStudentCanJoin : false,
      leaguePositionsCount: this.isExistEvent() ? schoolEvent.leaguePositionsCount : 0,
      replyToEmail: this.isExistEvent() ? propz.get(schoolEvent, ['replyToEmail'], '') : ''
    };

    const isShowShortDescription = !this.isExistEvent() || (this.isExistEvent() && !isClubEvent(schoolEvent));

    return (
      <div className="eSchoolEventForm">
        {this.renderCreateVenueForm()}

        <div className="eSchoolEventForm__shell">
          <div className="eSchoolEventForm__content">
            <Formik
              initialValues={eventInitial}
              validationSchema={this.getEventSchema()}
              validate={values => {
                const errors: any = {};
                if (typeof this.state.venue === 'undefined') {
                  errors.venue = 'Required';
                }
                return errors;
              }}
              onSubmit={values => {
                const requiredActions = getEventDetailsRequiredActions({ ...values, venue: values.venue || venue });
                if (requiredActions.length > 0) {
                  return;
                }

                console.log('SUBMIT CALLED', values);
                const { gender, ages, picUrl, shortDescription, replyToEmail, ...rest } = values;

                const newVenue = { ...venue };
                const isTBD = propz.get(values.venue, ['id']) === 'TBD';
                if (!isTBD && typeof point !== 'undefined') {
                  newVenue.point = { ...point };
                }

                const normalizedPicUrl = typeof picUrl === 'string' ? picUrl.trim() : '';
                const dataToSubmit = {
                  ...rest,
                  venue: newVenue,
                  shortDescription,
                  gender: getGendersFromMultiSelect(gender),
                  ages: getAgesFromMultiSelect(activeSchool, forms, ages),
                  photos: normalizedPicUrl ? [{ picUrl: normalizedPicUrl }] : [],
                  replyToEmail
                };

                onSubmit(dataToSubmit);
              }}
            >
              {({ touched, errors, setFieldValue, values, submitForm, validateForm }) => (
                <Form>
                  <EventDraftObserver values={values} onChange={this.props.onValuesChange} />
                  <EventDetailsPageHelp
                    values={values}
                    venue={venue}
                    user={user}
                    isEditing={this.isExistEvent()}
                    isShowAllSports={isShowAllSports}
                    isActivitySourceToggleVisible={isSchoolHaveFavoriteSports && isFavoriteSportsEnabled}
                    isShortDescriptionVisible={isShowShortDescription}
                    isEventTypeLocked={isAggregationTypeExist}
                  />
                  <section className="eSchoolEventForm__section eSchoolEventForm__sectionBasicDetails">
                    <div className="eSchoolEventForm__sectionHeader">
                      <div className="eSchoolEventForm__sectionTitleRow">
                        <span
                          className="eSchoolEventForm__sectionIcon eSchoolEventForm__sectionIconCalendar"
                          aria-hidden="true"
                        />

                        <div className="eSchoolEventForm__sectionTitleBlock">
                          <h2 className="eSchoolEventForm__sectionTitle">Basic details</h2>
                          <div className="eSchoolEventForm__sectionSubtitle">When it happens and what it is.</div>
                        </div>
                      </div>
                    </div>

                    <div className="eSchoolEventForm__basicCompactLayout">
                      <div className="eSchoolEventForm__basicTopRow" data-help-anchor="event-details-time">
                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldDate">
                          <Field name="startTime">
                            {({ field }) => (
                              <SitDatePicker
                                centered
                                label="Date"
                                value={field.value}
                                onChange={(date: Date | null) => {
                                  if (!date) return;

                                  const nextStartTime = this.applyDatePart(values.startTime, date);
                                  const nextEndTime = this.applyDatePart(
                                    values.endTime || this.addOneHour(values.startTime),
                                    date
                                  );

                                  setFieldValue('startTime', nextStartTime);
                                  setFieldValue('endTime', nextEndTime);
                                }}
                                format="DD MMM YYYY"
                                required
                                clearable={false}
                                className="eSchoolEventForm__datePicker"
                              />
                            )}
                          </Field>
                        </div>

                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldStart">
                          <Field name="startTime">
                            {({ field }) => (
                              <TimePicker
                                label="Start time"
                                value={field.value}
                                onChange={(time: Date | null) => {
                                  if (!time) return;

                                  const nextStartTime = this.applyTimePart(values.startTime, time);
                                  const nextEndTime = this.addOneHour(nextStartTime);

                                  setFieldValue('startTime', nextStartTime);
                                  setFieldValue('endTime', nextEndTime);
                                }}
                                minuteStep={5}
                                required
                                showConfirm
                                clearable={false}
                                className="eSchoolEventForm__timePicker"
                              />
                            )}
                          </Field>
                        </div>

                        <div className="eSchoolEventForm__basicDateField eSchoolEventForm__basicDateFieldEnd">
                          <Field name="endTime">
                            {({ field }) => (
                              <TimePicker
                                label="Finish / collection"
                                value={field.value}
                                onChange={(time: Date | null) => {
                                  if (!time) return;

                                  setFieldValue(
                                    'endTime',
                                    this.applyTimePart(values.endTime || values.startTime, time)
                                  );
                                }}
                                minuteStep={5}
                                required
                                showConfirm
                                clearable={false}
                                className="eSchoolEventForm__timePicker"
                                error={touched.endTime && errors.endTime ? String(errors.endTime) : undefined}
                              />
                            )}
                          </Field>
                        </div>
                      </div>

                      {isShowShortDescription && (
                        <div className="eSchoolEventForm__basicField" data-help-anchor="event-details-description">
                          <label className="eSchoolEventForm__label">Short description</label>
                          <Field
                            type="text"
                            name="shortDescription"
                            className="form-control"
                            placeholder="Optional — a name is generated automatically if left blank"
                          />
                        </div>
                      )}

                      <div className="eSchoolEventForm__sportRow">
                        <div className="eSchoolEventForm__sportMain" data-help-anchor="event-details-activity">
                          <div className="eSchoolEventForm__sportHeader">
                            <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired mb-0">
                              Activity / Sport
                            </label>

                            {isSchoolHaveFavoriteSports && isFavoriteSportsEnabled && (
                              <div className="eSchoolEventForm__sportMode" role="tablist" aria-label="Sport source">
                                <button
                                  type="button"
                                  className={
                                    !isShowAllSports
                                      ? 'eSchoolEventForm__sportModeBtn eSchoolEventForm__sportModeBtnActive'
                                      : 'eSchoolEventForm__sportModeBtn'
                                  }
                                  onClick={() => {
                                    const isActiveSportFavorite = propz.get(values, ['sport', 'isFavorite'], false);

                                    if (!isActiveSportFavorite) {
                                      setFieldValue('sportId', '');
                                      setFieldValue('sport', undefined);
                                    }

                                    this.setState({ isShowAllSports: false });
                                  }}
                                >
                                  Favourites
                                </button>

                                <button
                                  type="button"
                                  className={
                                    isShowAllSports
                                      ? 'eSchoolEventForm__sportModeBtn eSchoolEventForm__sportModeBtnActive'
                                      : 'eSchoolEventForm__sportModeBtn'
                                  }
                                  onClick={() => {
                                    this.setState({ isShowAllSports: true });
                                  }}
                                >
                                  All activities
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="eSchoolEventForm__sportInputLine">
                            <div className="eSchoolEventForm__sportAutocomplete">
                              <Field name="sport">
                                {({ field }) => {
                                  return (
                                    <Autocomplete
                                      searchFunction={text => this.getSports(text, setFieldValue)}
                                      getElementTitle={getName}
                                      customClass="mFullWidth"
                                      defaultItem={values.sport}
                                      placeholder="Start typing the Activity/Sport name"
                                      onInputChange={text => {
                                        const selectedName = propz.get(values, ['sport', 'name'], '');
                                        this.setState({ activityInputText: text });

                                        if (values.sportId && text !== selectedName) {
                                          setFieldValue('sportId', '');
                                          setFieldValue('sport', undefined);
                                          setFieldValue('multiparty', false);
                                          setFieldValue('gender', getDefaultGendersForMultiSelect());
                                          this.setState({ multiparty: false });
                                        }
                                      }}
                                      onSelect={sport => {
                                        const isMultipartySport = propz.get(sport, ['multiparty'], false);
                                        const gender = getGendersForMultiSelect(sport);

                                        setFieldValue('sportId', sport.id);
                                        setFieldValue('sport', sport);
                                        setFieldValue('participants', []);
                                        setFieldValue('houses', []);
                                        setFieldValue('multiparty', isMultipartySport);
                                        setFieldValue('gender', gender);

                                        this.setState({
                                          multiparty: isMultipartySport,
                                          houses: [],
                                          participants: [],
                                          activityInputText: getName(sport)
                                        });
                                      }}
                                    />
                                  );
                                }}
                              </Field>
                              {touched.sportId && errors.sportId ? (
                                <div className="alert alert-danger mt-2">{errors.sportId}</div>
                              ) : null}
                              {!values.sportId && this.state.activityInputText.trim() ? (
                                <div className="eSchoolEventForm__activitySelectionWarning" role="alert">
                                  <span className="eSchoolEventForm__activitySelectionWarningIcon" aria-hidden="true">
                                    !
                                  </span>
                                  <span>
                                    <strong>This activity has not been selected yet.</strong> Choose the matching
                                    Activity / Sport from the suggestions. Typing its name alone does not select it.
                                  </span>
                                </div>
                              ) : (
                                <div className="eSchoolEventForm__activityHint">
                                  Start typing, then select an Activity / Sport from the list to unlock Gender and Age
                                  groups.
                                </div>
                              )}
                            </div>

                            <div className="eSchoolEventForm__sportToggles">
                              <div
                                className="eSchoolEventForm__inlineOption"
                                data-help-anchor="event-details-multiple-teams"
                              >
                                <div className="eSchoolEventForm__inlineOptionText">Multiple teams</div>
                                <Field name="multiparty">
                                  {({ field }) => (
                                    <Switch
                                      {...field}
                                      value={multiparty}
                                      customClass="mb-0"
                                      disabled={!values.sportId}
                                      onChange={e => this.onMultipartyChange(e, setFieldValue, values)}
                                    />
                                  )}
                                </Field>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="eSchoolEventForm__section eSchoolEventForm__audienceCard">
                    <div className="eSchoolEventForm__audienceHeader">
                      <span className="eSchoolEventForm__audienceIcon">{this.renderUsersIcon()}</span>
                      <div className="eSchoolEventForm__audienceTitleBlock">
                        <div className="eSchoolEventForm__audienceTitle">Audience</div>
                        <div className="eSchoolEventForm__audienceDescription">
                          Who the event is for — this narrows the participant list next.
                        </div>
                      </div>
                    </div>

                    <div className="eSchoolEventForm__audienceDivider" />

                    {!values.sportId && (
                      <button
                        type="button"
                        className="eSchoolEventForm__audienceLockedNotice"
                        onClick={() => scrollToEventHelpAnchor('event-details-activity')}
                      >
                        <span className="eSchoolEventForm__audienceLockedIcon" aria-hidden="true">
                          i
                        </span>
                        <span>
                          <strong>Gender and Age groups are locked.</strong> Select an Activity / Sport from the
                          suggestions above to continue.
                        </span>
                        <span className="eSchoolEventForm__audienceLockedAction">Choose activity ↑</span>
                      </button>
                    )}

                    <div className="eSchoolEventForm__basicAudienceRow">
                      <div
                        className="eSchoolEventForm__basicAudienceItem eSchoolEventForm__basicAudienceItemGender"
                        data-help-anchor="event-details-gender"
                      >
                        <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired">Gender</label>
                        <div className="eSchoolEventForm__chipGroup">
                          {(values.gender || []).map((item, index) => (
                            <button
                              key={`${item.name}-${index}`}
                              type="button"
                              className={`eSchoolEventForm__audienceChip ${item.isChecked ? 'is-selected' : ''}`}
                              disabled={!values.sportId}
                              onClick={() => this.onGenderChange(index, setFieldValue, values)}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                        {touched.gender && errors.gender ? (
                          <div className="alert alert-danger mt-2">{String(errors.gender)}</div>
                        ) : null}
                      </div>

                      <div
                        className="eSchoolEventForm__basicAudienceItem eSchoolEventForm__basicAudienceItemAge"
                        data-help-anchor="event-details-ages"
                      >
                        <label className="eSchoolEventForm__label">Age groups</label>
                        <div className="eSchoolEventForm__ageStageList">
                          {getAgeKeyStageGroups(values.ages || []).map(group => (
                            <div key={group.label} className="eSchoolEventForm__ageStage">
                              <div className="eSchoolEventForm__ageStageLabel">{group.label}</div>
                              <div className="eSchoolEventForm__chipGroup eSchoolEventForm__chipGroupAges">
                                {group.items.map(({ item, index }) => (
                                  <button
                                    key={String(item.age || item.name || index)}
                                    type="button"
                                    className={`eSchoolEventForm__audienceChip ${item.isChecked ? 'is-selected' : ''}`}
                                    disabled={!values.sportId}
                                    onClick={() => this.onAgesChange(index, setFieldValue, values)}
                                  >
                                    {item.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        {touched.ages && errors.ages ? (
                          <div className="alert alert-danger mt-2">{String(errors.ages)}</div>
                        ) : null}
                      </div>
                    </div>
                  </section>
                  <section className="eSchoolEventForm__section eSchoolEventForm__sectionEventSetup">
                    <div className="eSchoolEventForm__sectionHeader">
                      <div className="eSchoolEventForm__sectionTitleRow">
                        <span
                          className="eSchoolEventForm__sectionIcon eSchoolEventForm__sectionIconFlag"
                          aria-hidden="true"
                        >
                          {this.renderFlagIcon()}
                        </span>
                        <div className="eSchoolEventForm__sectionTitleBlock">
                          <h2 className="eSchoolEventForm__sectionTitle">Event type & participants</h2>
                          <div className="eSchoolEventForm__sectionSubtitle">Who you’re playing.</div>
                        </div>
                      </div>
                    </div>

                    <div className="eSchoolEventForm__eventSetupBody">
                      <div className="eSchoolEventForm__eventTypeField" data-help-anchor="event-details-type">
                        <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired">Event type</label>
                        <div className="eSchoolEventForm__eventTypeChips" role="radiogroup" aria-label="Event type">
                          {(isAggregationTypeExist
                            ? [
                                {
                                  value: AGGREGATION_AND_EVENT_TYPES_MAPPING[aggregationType],
                                  label: AGGREGATION_AND_EVENT_TYPES_SERVER_TO_CLIENT_MAPPING[aggregationType],
                                  icon: this.renderFlagIcon()
                                }
                              ]
                            : [
                                {
                                  value: EVENT_TYPES.EXTERNAL_SCHOOLS,
                                  label: EVENT_TYPES_SERVER_TO_CLIENT_MAPPING.EXTERNAL_SCHOOLS,
                                  icon: this.renderFlagIcon()
                                },
                                {
                                  value: EVENT_TYPES.INTERNAL_HOUSES,
                                  label: EVENT_TYPES_SERVER_TO_CLIENT_MAPPING.INTERNAL_HOUSES,
                                  icon: this.renderHomeIcon()
                                },
                                {
                                  value: EVENT_TYPES.INTERNAL_TEAMS,
                                  label: EVENT_TYPES_SERVER_TO_CLIENT_MAPPING.INTERNAL_TEAMS,
                                  icon: this.renderShieldIcon()
                                }
                              ]
                          ).map(option => {
                            const isSelected = values.eventType === option.value;

                            return (
                              <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                className={`eSchoolEventForm__eventTypeChip ${isSelected ? 'is-selected' : ''}`}
                                disabled={isAggregationTypeExist}
                                onClick={() => {
                                  if (isAggregationTypeExist || isSelected) {
                                    return;
                                  }

                                  setFieldValue('eventType', option.value);
                                  this.setState({
                                    eventType: option.value
                                  });
                                }}
                              >
                                <span className="eSchoolEventForm__eventTypeChipIcon">{option.icon}</span>
                                <span>{option.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        {touched.eventType && errors.eventType ? (
                          <div className="alert alert-danger mt-2">{errors.eventType}</div>
                        ) : null}
                      </div>

                      {values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && (
                        <div
                          className="eSchoolEventForm__participantPanel"
                          data-help-anchor="event-details-participants"
                        >
                          <div className="eSchoolEventForm__participantGrid">
                            <div className="eSchoolEventForm__participantMain">
                              <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired">
                                Add opponent schools
                              </label>
                              <Field name="participants">
                                {() => (
                                  <Autocomplete
                                    searchFunction={this.getSchools}
                                    getElementTitle={getName}
                                    customClass="mFullWidth eSchoolEventForm__eventAutocomplete"
                                    defaultItem={undefined}
                                    placeholder="Start typing the school name"
                                    isItemWithImage={true}
                                    clearOnSelect={true}
                                    disabled={multiparty ? false : values.participants.length > 0}
                                    onSelect={participant => {
                                      const nextParticipants = [...values.participants, participant];
                                      setFieldValue('participants', nextParticipants);
                                      this.setState({
                                        participants: nextParticipants
                                      });
                                    }}
                                  />
                                )}
                              </Field>
                              {touched.participants && errors.participants ? (
                                <div className="alert alert-danger mt-2">{String(errors.participants)}</div>
                              ) : null}
                              <div className="eEntityStrip eSchoolEventForm__entityStripDs">
                                {this.renderParticipants(values.participants, setFieldValue)}
                              </div>
                            </div>

                            {isExistActiveSchoolPostcode && (
                              <div className="eSchoolEventForm__participantAside">
                                <label className="eSchoolEventForm__label">Venue distance</label>
                                <Field
                                  component="select"
                                  name="distance"
                                  className="form-control eSchoolEventForm__eventSelect"
                                  onChange={event => {
                                    const nextDistance = Number.parseInt(event.target.value);
                                    setFieldValue('distance', nextDistance);
                                    this.setState({
                                      distance: nextDistance
                                    });
                                  }}
                                >
                                  <option value={VENUE_DISTANCE.UNLIMITED}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING.UNLIMITED}
                                  </option>
                                  <option value={VENUE_DISTANCE['10M']}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['10M']}
                                  </option>
                                  <option value={VENUE_DISTANCE['20M']}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['20M']}
                                  </option>
                                  <option value={VENUE_DISTANCE['30M']}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['30M']}
                                  </option>
                                  <option value={VENUE_DISTANCE['40M']}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['40M']}
                                  </option>
                                  <option value={VENUE_DISTANCE['50M']}>
                                    {VENUE_DISTANCE_SERVER_TO_CLIENT_MAPPING['50M']}
                                  </option>
                                </Field>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {values.eventType === EVENT_TYPES.INTERNAL_HOUSES && (
                        <div
                          className="eSchoolEventForm__participantPanel"
                          data-help-anchor="event-details-participants"
                        >
                          <div className="eSchoolEventForm__participantMain eSchoolEventForm__participantMainHouses">
                            <div className="eSchoolEventForm__participantLabelRow">
                              <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired">
                                Add houses
                              </label>
                              {multiparty && (
                                <Field name="setAllHouses">
                                  {() => (
                                    <button
                                      type="button"
                                      className="eSchoolEventForm__selectAllBtn"
                                      onClick={() => {
                                        this.onAllHousesClick(setFieldValue);
                                      }}
                                    >
                                      {isSelectAllHouses ? 'Clear all' : 'Add all'}
                                    </button>
                                  )}
                                </Field>
                              )}
                            </div>
                            <Field name="houses">
                              {() => (
                                <Autocomplete
                                  searchFunction={this.getHouses}
                                  getElementTitle={getName}
                                  customClass="mFullWidth eSchoolEventForm__eventAutocomplete"
                                  defaultItem={undefined}
                                  placeholder="Start typing the house name"
                                  isItemWithImage={true}
                                  onSelect={house => {
                                    const nextHouses = [...values.houses, house];
                                    const { allHouses } = this.state;

                                    setFieldValue('houses', nextHouses);
                                    this.setState({
                                      houses: nextHouses,
                                      isSelectAllHouses: nextHouses.length === allHouses.length
                                    });
                                  }}
                                  disabled={multiparty ? false : values.houses.length >= 2}
                                />
                              )}
                            </Field>
                            <div className="eSchoolEventForm__participantHint">
                              {multiparty
                                ? 'Add houses one at a time, or all houses at once.'
                                : 'Add houses one at a time. Turn on Multiple teams to add every house.'}
                            </div>
                            {touched.houses && errors.houses ? (
                              <div className="alert alert-danger mt-2">{String(errors.houses)}</div>
                            ) : null}
                            <div className="eEntityStrip eSchoolEventForm__entityStripDs">
                              {this.renderHouses(values.houses, setFieldValue)}
                            </div>
                          </div>
                        </div>
                      )}

                      {values.eventType === EVENT_TYPES.INTERNAL_TEAMS && (
                        <div
                          className="eSchoolEventForm__participantNotice"
                          data-help-anchor="event-details-participants"
                        >
                          Internal event - you will pick your own students in the next step.
                        </div>
                      )}
                    </div>
                  </section>
                  <section className="eSchoolEventForm__section eSchoolEventForm__sectionVenueContact">
                    <div className="eSchoolEventForm__sectionHeader">
                      <div className="eSchoolEventForm__sectionTitleRow">
                        <span
                          className="eSchoolEventForm__sectionIcon eSchoolEventForm__sectionIconPin"
                          aria-hidden="true"
                        >
                          {this.renderPinIcon()}
                        </span>
                        <div className="eSchoolEventForm__sectionTitleBlock">
                          <h2 className="eSchoolEventForm__sectionTitle">Venue & contact</h2>
                          <div className="eSchoolEventForm__sectionSubtitle">
                            Where it is and who replies get sent to.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="eSchoolEventForm__venueContactBody">
                      <div className="eSchoolEventForm__venueContactGrid">
                        <div className="eSchoolEventForm__venueContactMain">
                          <div className="form-group mb-0" data-help-anchor="event-details-venue">
                            <label className="eSchoolEventForm__label eSchoolEventForm__labelRequired">Venue</label>

                            <div className="eSchoolEventForm__venueRow">
                              <div className="eSchoolEventForm__venueInput">
                                <Field name="venue">
                                  {() => (
                                    <Autocomplete
                                      searchFunction={text =>
                                        getPostcodes(
                                          text,
                                          user,
                                          values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS,
                                          this.state.participants
                                        )
                                      }
                                      getElementTitle={getNameByPlaceOrPostcode}
                                      customClass="mFullWidth mb-0"
                                      placeholder="Start typing the venue name"
                                      defaultItem={this.state.venue}
                                      disabled={
                                        values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS &&
                                        values.participants.length === 0
                                      }
                                      onSelect={venueItem => {
                                        let postcode = venueItem;

                                        if (isPlace(postcode)) {
                                          postcode = convertPlaceToPostcodeWithPlaceName(postcode);
                                        }

                                        setFieldValue('venue', postcode);
                                        this.setState({
                                          venue: postcode
                                        });
                                        this.getNewPoint(postcode.point);
                                      }}
                                    />
                                  )}
                                </Field>
                              </div>

                              <button
                                type="button"
                                className="btn btn-outline-secondary eSchoolEventForm__venueBtn"
                                onClick={this.createVenueFormOpen}
                                disabled={
                                  values.eventType === EVENT_TYPES.EXTERNAL_SCHOOLS && values.participants.length === 0
                                }
                              >
                                Create venue
                              </button>
                            </div>

                            {touched.venue && errors.venue ? (
                              <div className="alert alert-danger mt-2">{String(errors.venue)}</div>
                            ) : null}
                          </div>

                          <div className="form-group mb-0" data-help-anchor="event-details-contact">
                            <label className="eSchoolEventForm__label">Reply-to email address</label>
                            <Field
                              type="email"
                              name="replyToEmail"
                              className="form-control eSchoolEventForm__contactInput"
                              placeholder="Optional — overrides the default school email"
                            />
                            {touched.replyToEmail && errors.replyToEmail && (
                              <div className="alert alert-danger mt-2">{errors.replyToEmail}</div>
                            )}
                          </div>
                        </div>

                        {propz.get(values.venue, ['venueType']) !== 'TBD' && typeof point !== 'undefined' && (
                          <div className="eSchoolEventForm__venueContactMap">
                            <Map
                              key={values.venue ? values.venue.id : 'emptyPostcode'}
                              point={point}
                              getNewPoint={this.getNewPoint}
                              customStylingClass="eSchoolEventVenueMap"
                              isMarkerDraggable={true}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                  <EventContinueActions
                    values={values}
                    venue={venue}
                    onCancel={this.props.onCancel}
                    onContinue={() => this.onAddNewTeamClick(setFieldValue, submitForm)}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }
}
